// Chemin: C:\gestion-copro\src\pages\admin\MessageriePage.tsx
// Page Messagerie - Copro Protect
// Session 53 : Ajout onglets (Recus / Envoyes / En cours) selon spec SENTINELLE V2
//   - Onglet "En cours" affiche les messages de messagesCS en attente de vote
//   - Interface de vote pour les membres CS
//   - Suppression du workflow qui creait dans courriers
// Session 71 : Ajout bouton "Envoyer directement" pour brouillons de nature "courante"
// Session 93 : Prefill depuis SENTINELLE (routage action send_email)

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Mail,
  Search,
  RefreshCw,
  Paperclip,
  Archive,
  Trash2,
  Reply,
  Plus,
  Loader2,
  AlertTriangle,
  CheckCircle,
  X,
  Send,
  Clock,
  Inbox,
  Users,
  ThumbsUp,
  ThumbsDown,
  Shield,
} from 'lucide-react';
import { Timestamp } from 'firebase/firestore';
import {
  initiateGmailAuth,
  disconnectGmail,
  subscribeToGmailStatus,
  fetchEmails,
  fetchEmailDetail,
  downloadAttachment,
  formatEmailDate,
  truncateSnippet,
  trashEmail,
  sendEmail,
} from '../../services/gmail.service';
import {
  GmailConnectionStatus,
  EmailHeader,
  EmailDetail,
  EmailSearchParams,
} from '../../types/gmail.types';
import {
  getMessagesEnCours,
  getMessagesEnvoyes,
  enregistrerVote,
  marquerCommeEnvoye,
} from '../../services/messageCS.service';
import { getMembresCSByCopropriete } from '../../services/conseilSyndical.service';
import { MessageCS, VoteMembreCS } from '../../types/messageCS.types';
import ArchiveEmailModal from '../../components/messagerie/ArchiveEmailModal';
import ReplyEmailModal from '../../components/messagerie/ReplyEmailModal';
import ComposeEmailModal from '../../components/messagerie/ComposeEmailModal';
import { useCopropriete } from '../../context/CoproprieteContext';
import { useAuth } from '../../context/AuthContext';

// ============================================================================
// TYPES
// ============================================================================

type OngletMessagerie = 'recus' | 'envoyes' | 'encours';

// ============================================================================
// COMPOSANT PRINCIPAL
// ============================================================================

const MessageriePage: React.FC = () => {
  const { coproprieteId } = useCopropriete();
  const { firebaseUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Session 93 : Récupérer le prefill SENTINELLE depuis location.state
  const sentinellePrefill = (location.state as any)?.fromSentinelle 
    ? (location.state as any)?.prefill 
    : null;

  // Onglet actif
  const [ongletActif, setOngletActif] = useState<OngletMessagerie>('recus');

  // Etat connexion Gmail
  const [connectionStatus, setConnectionStatus] = useState<GmailConnectionStatus>({
    isConnected: false,
  });
  const [isConnecting, setIsConnecting] = useState(false);

  // Etat liste emails (onglet Recus)
  const [emails, setEmails] = useState<EmailHeader[]>([]);
  const [isLoadingEmails, setIsLoadingEmails] = useState(false);
  const [nextPageToken, setNextPageToken] = useState<string | undefined>();
  const [totalEstimate, setTotalEstimate] = useState<number | undefined>();

  // Etat detail email
  const [selectedEmail, setSelectedEmail] = useState<EmailDetail | null>(null);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);

  // Etat recherche
  const [searchQuery, setSearchQuery] = useState('');
  const [searchParams, setSearchParams] = useState<EmailSearchParams>({});

  // Etat messages en cours (onglet En cours)
  const [messagesEnCours, setMessagesEnCours] = useState<MessageCS[]>([]);
  const [isLoadingMessagesEnCours, setIsLoadingMessagesEnCours] = useState(false);
  const [selectedMessageCS, setSelectedMessageCS] = useState<MessageCS | null>(null);

  // Etat messages envoyes (onglet Envoyes)
  const [messagesEnvoyes, setMessagesEnvoyes] = useState<MessageCS[]>([]);
  const [isLoadingMessagesEnvoyes, setIsLoadingMessagesEnvoyes] = useState(false);

  // Nombre de membres CS
  const [nombreMembresCS, setNombreMembresCS] = useState(3);

  // Etat vote en cours
  const [isVoting, setIsVoting] = useState(false);
  const [voteComment, setVoteComment] = useState('');

  // SESSION 71 : Etat envoi direct brouillon (communication courante)
  const [isSendingDirect, setIsSendingDirect] = useState(false);

  // Etat erreurs
  const [error, setError] = useState<string | null>(null);

  // Etat modale archivage
  const [showArchiveModal, setShowArchiveModal] = useState(false);

  // Etat modale suppression
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Etat modale reponse
  const [showReplyModal, setShowReplyModal] = useState(false);

  // Etat modale nouveau message
  const [showComposeModal, setShowComposeModal] = useState(false);

  // Etat toast succes
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Session 93 : État prefill SENTINELLE pour ComposeEmailModal
  const [sentinelleEmailPrefill, setSentinelleEmailPrefill] = useState<{
    destinataire: string;
    objet: string;
    contenu: string;
  } | null>(null);

  // ============================================================================
  // EFFETS
  // ============================================================================

  // Session 93 : Ouvrir automatiquement ComposeEmailModal si prefill SENTINELLE
  useEffect(() => {
    if (sentinellePrefill) {
      setSentinelleEmailPrefill({
        destinataire: sentinellePrefill.destinataire || '',
        objet: sentinellePrefill.objet || '',
        contenu: sentinellePrefill.contenu || '',
      });
      setShowComposeModal(true);
      // Nettoyer le state de navigation pour éviter un re-prefill
      window.history.replaceState({}, document.title);
    }
  }, [sentinellePrefill]);

  // Ecouter le statut de connexion Gmail
  useEffect(() => {
    if (!coproprieteId) return;
    
    const unsubscribe = subscribeToGmailStatus(coproprieteId, (status) => {
      setConnectionStatus(status);
    });

    return () => unsubscribe();
  }, [coproprieteId]);

  // Charger les emails quand connecte (onglet Recus)
  // Fix session 242 : garde isLoadingEmails pour eviter appels simultanees en boucle
  useEffect(() => {
    if (connectionStatus.isConnected && coproprieteId && ongletActif === 'recus' && !isLoadingEmails) {
      loadEmails();
    }
  }, [connectionStatus.isConnected, coproprieteId, ongletActif]);

  // Charger le nombre de membres CS
  useEffect(() => {
    if (!coproprieteId) return;
    
    const loadMembresCS = async () => {
      try {
        const membres = await getMembresCSByCopropriete(coproprieteId);
        const actifs = membres.filter((m: { actif?: boolean }) => m.actif !== false);
        setNombreMembresCS(actifs.length > 0 ? actifs.length : 3);
      } catch (err) {
        console.error('Erreur chargement membres CS:', err);
      }
    };
    loadMembresCS();
  }, [coproprieteId]);

  // Auto-masquer le message de succes
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // ============================================================================
  // CHARGEMENT DONNEES
  // ============================================================================

  // Charger les emails Gmail (Recus)
  const loadEmails = useCallback(
    async (params?: EmailSearchParams, append = false) => {
      if (!coproprieteId) return;
      
      setIsLoadingEmails(true);
      setError(null);

      try {
        const result = await fetchEmails(coproprieteId, params);

        if (append) {
          setEmails((prev) => [...prev, ...result.emails]);
        } else {
          setEmails(result.emails);
        }

        setNextPageToken(result.nextPageToken);
        setTotalEstimate(result.totalEstimate);
      } catch (err) {
        console.error('Erreur chargement emails:', err);
        setError(
          err instanceof Error ? err.message : 'Erreur lors du chargement des emails.'
        );
      } finally {
        setIsLoadingEmails(false);
      }
    },
    [coproprieteId]
  );

  // Charger les messages en cours (messagesCS)
  const loadMessagesEnCours = useCallback(async () => {
    if (!coproprieteId) return;
    
    setIsLoadingMessagesEnCours(true);
    setError(null);

    try {
      const messages = await getMessagesEnCours(coproprieteId);
      setMessagesEnCours(messages);
    } catch (err) {
      console.error('Erreur chargement messages en cours:', err);
      setError(
        err instanceof Error ? err.message : 'Erreur lors du chargement des messages.'
      );
    } finally {
      setIsLoadingMessagesEnCours(false);
    }
  }, [coproprieteId]);

  // Charger les messages envoyes (messagesCS)
  const loadMessagesEnvoyes = useCallback(async () => {
    if (!coproprieteId) return;
    
    setIsLoadingMessagesEnvoyes(true);
    setError(null);

    try {
      const messages = await getMessagesEnvoyes(coproprieteId);
      setMessagesEnvoyes(messages);
    } catch (err) {
      console.error('Erreur chargement messages envoyes:', err);
      setError(
        err instanceof Error ? err.message : 'Erreur lors du chargement des messages.'
      );
    } finally {
      setIsLoadingMessagesEnvoyes(false);
    }
  }, [coproprieteId]);

  // SESSION 71 : Charger les messages en cours et envoyés AU MONTAGE (pour les compteurs)
  useEffect(() => {
    if (coproprieteId) {
      loadMessagesEnCours();
      loadMessagesEnvoyes();
    }
  }, [coproprieteId, loadMessagesEnCours, loadMessagesEnvoyes]);

  // ============================================================================
  // HANDLERS EMAILS GMAIL
  // ============================================================================

  // Charger plus d'emails (pagination)
  const loadMoreEmails = () => {
    if (nextPageToken) {
      loadEmails({ ...searchParams, pageToken: nextPageToken }, true);
    }
  };

  // Rechercher des emails
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const params: EmailSearchParams = searchQuery ? { query: searchQuery } : {};
    setSearchParams(params);
    setSelectedEmail(null);
    loadEmails(params);
  };

  // Charger le detail d'un email
  const handleSelectEmail = async (email: EmailHeader) => {
    if (!coproprieteId) return;
    
    setIsLoadingDetail(true);
    setError(null);

    try {
      const detail = await fetchEmailDetail(coproprieteId, email.id);
      setSelectedEmail(detail);

      // Marquer comme lu dans la liste locale
      setEmails((prev) =>
        prev.map((e) => (e.id === email.id ? { ...e, isRead: true } : e))
      );
    } catch (err) {
      console.error('Erreur chargement detail:', err);
      setError(
        err instanceof Error ? err.message : 'Erreur lors du chargement de l\'email.'
      );
    } finally {
      setIsLoadingDetail(false);
    }
  };

  // Connecter Gmail
  const handleConnectGmail = async () => {
    if (!coproprieteId) return;
    
    setIsConnecting(true);
    setError(null);

    try {
      await initiateGmailAuth(coproprieteId);
    } catch (err) {
      console.error('Erreur connexion Gmail:', err);
      setError(
        err instanceof Error ? err.message : 'Erreur lors de la connexion a Gmail.'
      );
      setIsConnecting(false);
    }
  };

  // Deconnecter Gmail
  const handleDisconnectGmail = async () => {
    if (!coproprieteId) return;
    
    if (!window.confirm('Voulez-vous vraiment deconnecter Gmail ?')) {
      return;
    }

    try {
      await disconnectGmail(coproprieteId);
      setEmails([]);
      setSelectedEmail(null);
    } catch (err) {
      console.error('Erreur deconnexion Gmail:', err);
      setError(
        err instanceof Error ? err.message : 'Erreur lors de la deconnexion.'
      );
    }
  };

  // Telecharger une piece jointe
  const handleDownloadAttachment = async (attachment: {
    id: string;
    filename: string;
    mimeType: string;
    size: number;
  }) => {
    if (!selectedEmail || !coproprieteId) return;

    try {
      await downloadAttachment(coproprieteId, selectedEmail.id, attachment);
    } catch (err) {
      console.error('Erreur telechargement:', err);
      setError(
        err instanceof Error
          ? err.message
          : 'Erreur lors du telechargement de la piece jointe.'
      );
    }
  };

  // Ouvrir la modale d'archivage
  const handleArchiveEmail = () => {
    if (selectedEmail) {
      setShowArchiveModal(true);
    }
  };

  // Callback succes archivage
  const handleArchiveSuccess = (message: string) => {
    setSuccessMessage(message);
    setShowArchiveModal(false);
    if (selectedEmail) {
      setEmails((prev) => prev.filter((e) => e.id !== selectedEmail.id));
      setSelectedEmail(null);
    }
  };

  // Ouvrir la modale de suppression
  const handleOpenDeleteModal = () => {
    if (selectedEmail) {
      setShowDeleteModal(true);
    }
  };

  // Supprimer l'email
  const handleDeleteEmail = async () => {
    if (!selectedEmail || !coproprieteId) return;

    setIsDeleting(true);

    try {
      await trashEmail(coproprieteId, selectedEmail.id);
      setSuccessMessage('Email deplace dans la corbeille');
      setShowDeleteModal(false);
      setEmails((prev) => prev.filter((e) => e.id !== selectedEmail.id));
      setSelectedEmail(null);
    } catch (err) {
      console.error('Erreur suppression:', err);
      setError(
        err instanceof Error ? err.message : 'Erreur lors de la suppression.'
      );
    } finally {
      setIsDeleting(false);
    }
  };

  // Ouvrir la modale de reponse
  const handleReplyEmail = () => {
    if (selectedEmail) {
      setShowReplyModal(true);
    }
  };

  // Callback succes reponse
  const handleReplySuccess = (message: string) => {
    setSuccessMessage(message);
    setShowReplyModal(false);
  };

  // Callback succes nouveau message
  const handleComposeSuccess = (message: string) => {
    setSuccessMessage(message);
    setShowComposeModal(false);
    // Recharger les messages en cours si on est sur cet onglet
    if (ongletActif === 'encours') {
      loadMessagesEnCours();
    }
  };

  // Transformer email en courrier formel
  const handleTransformToCourrier = (emailData: {
    to: string;
    subject: string;
    body: string;
    destinataireNom: string;
  }) => {
    setShowComposeModal(false);
    sessionStorage.setItem('courrierFromEmail', JSON.stringify({
      objet: emailData.subject,
      contenu: emailData.body,
      destinataireNom: emailData.destinataireNom,
      destinataireEmail: emailData.to,
    }));
    navigate('/admin/courriers/nouveau');
    setSuccessMessage('Redirection vers creation de courrier formel...');
  };

  // ============================================================================
  // HANDLERS VOTE CS (onglet En cours)
  // ============================================================================

  // Selectionner un message en cours
  const handleSelectMessageCS = (message: MessageCS) => {
    setSelectedMessageCS(message);
    setVoteComment('');
  };

  // Voter pour un message
  const handleVote = async (decision: 'pour' | 'contre') => {
    if (!coproprieteId || !selectedMessageCS || !firebaseUser || !userProfile) return;

    setIsVoting(true);
    setError(null);

    try {
      const vote: VoteMembreCS = {
        membreId: firebaseUser.uid,
        membreNom: userProfile.prenom 
          ? `${userProfile.prenom} ${userProfile.nom}` 
          : userProfile.nom || firebaseUser.email || 'Membre CS',
        vote: decision,
        dateVote: Timestamp.now(),
      };

      // Ajouter commentaire seulement s'il n'est pas vide
      if (voteComment.trim()) {
        vote.commentaire = voteComment.trim();
      }

      const result = await enregistrerVote(coproprieteId, selectedMessageCS.id, vote);

      if (result.nouveauStatut === 'valide') {
        // Unanimite atteinte → Envoyer le message via Gmail
        setSuccessMessage('Vote enregistre. Unanimite atteinte ! Envoi en cours...');
        
        try {
          const gmailResult = await sendEmail(coproprieteId, {
            to: selectedMessageCS.destinataireEmail,
            subject: selectedMessageCS.objet,
            body: selectedMessageCS.contenu,
            userId: selectedMessageCS.redacteurId,
            userNom: selectedMessageCS.redacteurNom,
          });

          // Marquer comme envoye dans Firestore
          await marquerCommeEnvoye(
            coproprieteId, 
            selectedMessageCS.id, 
            gmailResult.messageId || 'sent',
            gmailResult.threadId
          );

          setSuccessMessage('Message approuve et envoye avec succes !');
        } catch (sendErr) {
          console.error('Erreur envoi apres vote:', sendErr);
          setError('Vote enregistre mais erreur lors de l\'envoi. Contactez l\'administrateur.');
        }
      } else if (result.nouveauStatut === 'refuse') {
        setSuccessMessage('Vote enregistre. Message refuse par le Conseil Syndical.');
      } else {
        setSuccessMessage('Vote enregistre. En attente des autres membres.');
      }

      // Recharger les messages
      loadMessagesEnCours();
      setSelectedMessageCS(null);
      setVoteComment('');
    } catch (err) {
      console.error('Erreur vote:', err);
      setError(
        err instanceof Error ? err.message : 'Erreur lors du vote.'
      );
    } finally {
      setIsVoting(false);
    }
  };

  // SESSION 71 : Envoi direct pour brouillons de nature "courante"
  const handleEnvoiDirect = async () => {
    if (!coproprieteId || !selectedMessageCS || !connectionStatus.isConnected) return;

    // Vérifier que l'email destinataire est renseigné
    if (!selectedMessageCS.destinataireEmail) {
      setError('Veuillez renseigner l\'email du destinataire avant d\'envoyer.');
      return;
    }

    setIsSendingDirect(true);
    setError(null);

    try {
      // Envoyer via Gmail
      const gmailResult = await sendEmail(coproprieteId, {
        to: selectedMessageCS.destinataireEmail,
        subject: selectedMessageCS.objet,
        body: selectedMessageCS.contenu,
        userId: selectedMessageCS.redacteurId,
        userNom: selectedMessageCS.redacteurNom,
      });

      // Marquer comme envoyé dans Firestore
      await marquerCommeEnvoye(
        coproprieteId,
        selectedMessageCS.id,
        gmailResult.messageId || 'sent',
        gmailResult.threadId
      );

      setSuccessMessage('Message envoyé avec succès !');
      loadMessagesEnCours();
      setSelectedMessageCS(null);
    } catch (err) {
      console.error('Erreur envoi direct:', err);
      setError(err instanceof Error ? err.message : 'Erreur lors de l\'envoi.');
    } finally {
      setIsSendingDirect(false);
    }
  };

  // Verifier si l'utilisateur a deja vote
  const hasUserVoted = (message: MessageCS): boolean => {
    if (!firebaseUser || !message.validationCS) return false;
    return message.validationCS.votes.some(v => v.membreId === firebaseUser.uid);
  };

  // Obtenir le vote de l'utilisateur
  const getUserVote = (message: MessageCS): 'pour' | 'contre' | null => {
    if (!firebaseUser || !message.validationCS) return null;
    const vote = message.validationCS.votes.find(v => v.membreId === firebaseUser.uid);
    return vote ? vote.vote : null;
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  // Badge de risque
  const renderRiskBadge = (risque: string) => {
    const config = {
      faible: { bg: 'bg-green-100', text: 'text-green-800', label: 'Faible' },
      moyen: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Moyen' },
      eleve: { bg: 'bg-red-100', text: 'text-red-800', label: 'Eleve' },
    };
    const c = config[risque as keyof typeof config] || config.moyen;
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${c.bg} ${c.text}`}>
        {c.label}
      </span>
    );
  };

  // Badge de statut
  const renderStatutBadge = (statut: string) => {
    const config = {
      brouillon: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Brouillon' },
      en_validation: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'En attente de vote' },
      valide: { bg: 'bg-green-100', text: 'text-green-800', label: 'Approuve' },
      envoye: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Envoye' },
      refuse: { bg: 'bg-red-100', text: 'text-red-800', label: 'Refuse' },
      expire: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Expire' },
    };
    const c = config[statut as keyof typeof config] || config.brouillon;
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${c.bg} ${c.text}`}>
        {c.label}
      </span>
    );
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Messagerie</h1>
          <p className="text-gray-500 text-sm">Boite mail du Conseil Syndical</p>
        </div>

        {/* Statut connexion et bouton nouveau message */}
        {connectionStatus.isConnected && (
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowComposeModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Nouveau message
            </button>
            
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <span className="w-2 h-2 bg-green-500 rounded-full"></span>
              {connectionStatus.email}
            </div>
            <button
              onClick={handleDisconnectGmail}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Deconnecter
            </button>
          </div>
        )}
      </div>

      {/* Toast succes */}
      {successMessage && (
        <div className="fixed top-4 right-4 z-50 flex items-center gap-2 px-4 py-3 bg-green-50 border border-green-200 rounded-lg shadow-lg animate-fade-in">
          <CheckCircle className="w-5 h-5 text-green-600" />
          <span className="text-green-800">{successMessage}</span>
          <button
            onClick={() => setSuccessMessage(null)}
            className="ml-2 text-green-600 hover:text-green-800"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Erreur */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
          <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-red-800">{error}</p>
            <button
              onClick={() => setError(null)}
              className="text-sm text-red-600 hover:text-red-800 underline mt-1"
            >
              Fermer
            </button>
          </div>
        </div>
      )}

      {/* Contenu principal */}
      {!connectionStatus.isConnected ? (
        // Ecran de connexion Gmail
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center max-w-md mx-auto mt-12">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Mail className="w-8 h-8 text-blue-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Connecter la messagerie
          </h2>
          <p className="text-gray-600 mb-6">
            Connectez le compte Gmail du Conseil Syndical pour gerer vos emails.
          </p>
          <button
            onClick={handleConnectGmail}
            disabled={isConnecting}
            className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            {isConnecting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Connexion en cours...
              </>
            ) : (
              <>
                <Mail className="w-5 h-5" />
                Connecter Gmail
              </>
            )}
          </button>
        </div>
      ) : (
        // Interface messagerie avec onglets
        <>
          {/* Onglets */}
          <div className="flex border-b border-gray-200 mb-4">
            <button
              onClick={() => { setOngletActif('recus'); setSelectedEmail(null); setSelectedMessageCS(null); }}
              className={`flex items-center gap-2 px-4 py-3 font-medium text-sm border-b-2 transition-colors ${
                ongletActif === 'recus'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Inbox className="w-4 h-4" />
              Recus
              {emails.length > 0 && (
                <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs">
                  {emails.length}
                </span>
              )}
            </button>
            <button
              onClick={() => { setOngletActif('envoyes'); setSelectedEmail(null); setSelectedMessageCS(null); }}
              className={`flex items-center gap-2 px-4 py-3 font-medium text-sm border-b-2 transition-colors ${
                ongletActif === 'envoyes'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Send className="w-4 h-4" />
              Envoyes
              {messagesEnvoyes.length > 0 && (
                <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs">
                  {messagesEnvoyes.length}
                </span>
              )}
            </button>
            <button
              onClick={() => { setOngletActif('encours'); setSelectedEmail(null); setSelectedMessageCS(null); }}
              className={`flex items-center gap-2 px-4 py-3 font-medium text-sm border-b-2 transition-colors ${
                ongletActif === 'encours'
                  ? 'border-purple-600 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Clock className="w-4 h-4" />
              En cours
              {messagesEnCours.length > 0 && (
                <span className="px-2 py-0.5 bg-purple-100 text-purple-600 rounded-full text-xs font-semibold">
                  {messagesEnCours.length}
                </span>
              )}
            </button>
          </div>

          {/* ================================================================
              ONGLET RECUS
          ================================================================ */}
          {ongletActif === 'recus' && (
            <>
              {/* Barre de recherche */}
              <div className="mb-4 flex gap-2">
                <form onSubmit={handleSearch} className="flex-1 flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Rechercher dans les emails..."
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Rechercher
                  </button>
                </form>
                <button
                  onClick={() => loadEmails(searchParams)}
                  disabled={isLoadingEmails}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                  title="Actualiser"
                >
                  <RefreshCw className={`w-5 h-5 ${isLoadingEmails ? 'animate-spin' : ''}`} />
                </button>
              </div>

              {/* Grille emails */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Liste des emails */}
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <div className="p-3 border-b border-gray-100 flex items-center justify-between">
                    <span className="text-sm text-gray-600">
                      {totalEstimate !== undefined
                        ? `${emails.length} sur ${totalEstimate} emails`
                        : `${emails.length} emails`}
                    </span>
                  </div>

                  <div className="divide-y divide-gray-100 max-h-[calc(100vh-380px)] overflow-y-auto">
                    {isLoadingEmails && emails.length === 0 ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
                      </div>
                    ) : emails.length === 0 ? (
                      <div className="py-12 text-center text-gray-500">
                        Aucun email trouve
                      </div>
                    ) : (
                      <>
                        {emails.map((email) => (
                          <button
                            key={email.id}
                            onClick={() => handleSelectEmail(email)}
                            className={`w-full text-left p-3 hover:bg-gray-50 transition-colors ${
                              selectedEmail?.id === email.id ? 'bg-blue-50' : ''
                            } ${!email.isRead ? 'bg-blue-25' : ''}`}
                          >
                            <div className="flex items-start gap-3">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <span
                                    className={`truncate ${
                                      !email.isRead
                                        ? 'font-semibold text-gray-900'
                                        : 'text-gray-700'
                                    }`}
                                  >
                                    {email.from}
                                  </span>
                                  {email.hasAttachments && (
                                    <Paperclip className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                                  )}
                                </div>
                                <div
                                  className={`truncate text-sm ${
                                    !email.isRead ? 'font-medium text-gray-900' : 'text-gray-600'
                                  }`}
                                >
                                  {email.subject || '(Sans objet)'}
                                </div>
                                <div className="text-xs text-gray-400 mt-1 truncate">
                                  {truncateSnippet(email.snippet, 60)}
                                </div>
                              </div>
                              <div className="text-xs text-gray-400 flex-shrink-0">
                                {formatEmailDate(email.date)}
                              </div>
                            </div>
                          </button>
                        ))}

                        {nextPageToken && (
                          <button
                            onClick={loadMoreEmails}
                            disabled={isLoadingEmails}
                            className="w-full py-3 text-sm text-blue-600 hover:bg-blue-50 transition-colors disabled:opacity-50"
                          >
                            {isLoadingEmails ? (
                              <Loader2 className="w-4 h-4 animate-spin mx-auto" />
                            ) : (
                              'Charger plus d\'emails'
                            )}
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* Detail de l'email */}
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col max-h-[calc(100vh-340px)]">
                  {isLoadingDetail ? (
                    <div className="flex-1 flex items-center justify-center">
                      <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
                    </div>
                  ) : selectedEmail ? (
                    <>
                      {/* Header email */}
                      <div className="p-4 border-b border-gray-100">
                        <div className="flex items-start justify-between gap-4 mb-3">
                          <h2 className="text-lg font-semibold text-gray-900 line-clamp-2">
                            {selectedEmail.subject || '(Sans objet)'}
                          </h2>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <button
                              onClick={handleReplyEmail}
                              className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Repondre"
                            >
                              <Reply className="w-5 h-5" />
                            </button>
                            <button
                              onClick={handleOpenDeleteModal}
                              className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                              title="Supprimer"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                            <button
                              onClick={handleArchiveEmail}
                              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                            >
                              <Archive className="w-4 h-4" />
                              Archiver
                            </button>
                          </div>
                        </div>

                        <div className="text-sm text-gray-600 space-y-1">
                          <div>
                            <span className="text-gray-500">De :</span>{' '}
                            <span className="font-medium">{selectedEmail.from}</span>{' '}
                            <span className="text-gray-400">&lt;{selectedEmail.fromEmail}&gt;</span>
                          </div>
                          <div>
                            <span className="text-gray-500">A :</span> {selectedEmail.to}
                          </div>
                          {selectedEmail.cc && (
                            <div>
                              <span className="text-gray-500">Cc :</span> {selectedEmail.cc}
                            </div>
                          )}
                          <div>
                            <span className="text-gray-500">Date :</span>{' '}
                            {selectedEmail.date.toLocaleDateString('fr-FR', {
                              weekday: 'long',
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </div>
                        </div>

                        {selectedEmail.attachments.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-gray-100">
                            <div className="text-sm text-gray-500 mb-2">
                              {selectedEmail.attachments.length} piece(s) jointe(s)
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {selectedEmail.attachments.map((attachment) => (
                                <button
                                  key={attachment.id}
                                  onClick={() => handleDownloadAttachment(attachment)}
                                  className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm transition-colors"
                                >
                                  <Paperclip className="w-4 h-4 text-gray-500" />
                                  <span className="truncate max-w-[150px]">
                                    {attachment.filename}
                                  </span>
                                  <span className="text-gray-400">
                                    ({Math.round(attachment.size / 1024)} Ko)
                                  </span>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Corps de l'email */}
                      <div className="flex-1 overflow-y-auto p-4">
                        {selectedEmail.body ? (
                          <div
                            className="prose prose-sm max-w-none"
                            dangerouslySetInnerHTML={{ __html: selectedEmail.body }}
                          />
                        ) : (
                          <div className="whitespace-pre-wrap text-gray-700">
                            {selectedEmail.bodyText || 'Aucun contenu'}
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="flex-1 flex items-center justify-center text-gray-500">
                      <div className="text-center">
                        <Mail className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                        <p>Selectionnez un email pour le lire</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {/* ================================================================
              ONGLET ENVOYES
          ================================================================ */}
          {ongletActif === 'envoyes' && (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="p-3 border-b border-gray-100 flex items-center justify-between">
                <span className="text-sm text-gray-600">
                  {messagesEnvoyes.length} message(s) envoye(s)
                </span>
                <button
                  onClick={loadMessagesEnvoyes}
                  disabled={isLoadingMessagesEnvoyes}
                  className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <RefreshCw className={`w-4 h-4 ${isLoadingMessagesEnvoyes ? 'animate-spin' : ''}`} />
                </button>
              </div>

              <div className="divide-y divide-gray-100 max-h-[calc(100vh-340px)] overflow-y-auto">
                {isLoadingMessagesEnvoyes ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
                  </div>
                ) : messagesEnvoyes.length === 0 ? (
                  <div className="py-12 text-center text-gray-500">
                    <Send className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>Aucun message envoye</p>
                  </div>
                ) : (
                  messagesEnvoyes.map((msg) => (
                    <div key={msg.id} className="p-4 hover:bg-gray-50">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-gray-900 truncate">
                              {msg.destinataireNom}
                            </span>
                            {renderRiskBadge(msg.analyseSentinelle.risque)}
                          </div>
                          <div className="text-sm text-gray-700 truncate">
                            {msg.objet}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            Envoye le {msg.dateEnvoi?.toDate().toLocaleDateString('fr-FR', {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Shield className="w-4 h-4 text-green-600" />
                          <span className="text-xs text-green-600">SENTINELLE</span>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* ================================================================
              ONGLET EN COURS (messages en attente de vote)
          ================================================================ */}
          {ongletActif === 'encours' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Liste des messages en cours */}
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="p-3 border-b border-gray-100 flex items-center justify-between">
                  <span className="text-sm text-gray-600">
                    {messagesEnCours.length} message(s) en attente
                  </span>
                  <button
                    onClick={loadMessagesEnCours}
                    disabled={isLoadingMessagesEnCours}
                    className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <RefreshCw className={`w-4 h-4 ${isLoadingMessagesEnCours ? 'animate-spin' : ''}`} />
                  </button>
                </div>

                <div className="divide-y divide-gray-100 max-h-[calc(100vh-380px)] overflow-y-auto">
                  {isLoadingMessagesEnCours ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
                    </div>
                  ) : messagesEnCours.length === 0 ? (
                    <div className="py-12 text-center text-gray-500">
                      <Clock className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>Aucun message en attente de vote</p>
                    </div>
                  ) : (
                    messagesEnCours.map((msg) => (
                      <button
                        key={msg.id}
                        onClick={() => handleSelectMessageCS(msg)}
                        className={`w-full text-left p-4 hover:bg-gray-50 transition-colors ${
                          selectedMessageCS?.id === msg.id ? 'bg-purple-50' : ''
                        }`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium text-gray-900 truncate">
                                {msg.destinataireNom}
                              </span>
                              {renderStatutBadge(msg.statut)}
                            </div>
                            <div className="text-sm text-gray-700 truncate">
                              {msg.objet}
                            </div>
                            <div className="flex items-center gap-3 mt-2">
                              <div className="flex items-center gap-1 text-xs text-gray-500">
                                <Users className="w-3 h-3" />
                                {msg.validationCS?.votes.length || 0}/{msg.validationCS?.votesRequis || nombreMembresCS} votes
                              </div>
                              {hasUserVoted(msg) && (
                                <div className={`flex items-center gap-1 text-xs ${
                                  getUserVote(msg) === 'pour' ? 'text-green-600' : 'text-red-600'
                                }`}>
                                  {getUserVote(msg) === 'pour' ? (
                                    <ThumbsUp className="w-3 h-3" />
                                  ) : (
                                    <ThumbsDown className="w-3 h-3" />
                                  )}
                                  Vous avez vote
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="text-xs text-gray-400">
                            {msg.createdAt?.toDate().toLocaleDateString('fr-FR')}
                          </div>
                        </div>
                      </button>
                    ))
                  )}
                </div>
              </div>

              {/* Detail du message et interface de vote */}
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col max-h-[calc(100vh-340px)]">
                {selectedMessageCS ? (
                  <>
                    {/* Header message */}
                    <div className="p-4 border-b border-gray-100">
                      <div className="flex items-start justify-between gap-4 mb-3">
                        <h2 className="text-lg font-semibold text-gray-900">
                          {selectedMessageCS.objet}
                        </h2>
                        <div className="flex items-center gap-2">
                          {renderRiskBadge(selectedMessageCS.analyseSentinelle.risque)}
                          {renderStatutBadge(selectedMessageCS.statut)}
                        </div>
                      </div>

                      <div className="text-sm text-gray-600 space-y-1">
                        <div>
                          <span className="text-gray-500">A :</span>{' '}
                          <span className="font-medium">{selectedMessageCS.destinataireNom}</span>{' '}
                          <span className="text-gray-400">&lt;{selectedMessageCS.destinataireEmail}&gt;</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Redige par :</span>{' '}
                          {selectedMessageCS.redacteurNom}
                        </div>
                      </div>
                    </div>

                    {/* Corps du message */}
                    <div className="flex-1 overflow-y-auto p-4">
                      <div className="whitespace-pre-wrap text-gray-700">
                        {selectedMessageCS.contenu}
                      </div>
                    </div>

                    {/* SESSION 71 : Bouton envoi direct pour brouillons de nature "courante" */}
                    {selectedMessageCS.statut === 'brouillon' && 
                     selectedMessageCS.analyseSentinelle?.nature === 'courante' && (
                      <div className="border-t border-gray-100 p-4 bg-blue-50">
                        <div className="flex items-start gap-3">
                          <div className="p-2 bg-blue-100 rounded-lg">
                            <Send className="w-5 h-5 text-blue-600" />
                          </div>
                          <div className="flex-1">
                            <h3 className="font-medium text-blue-900 mb-1">
                              Communication courante
                            </h3>
                            <p className="text-sm text-blue-700 mb-3">
                              Ce message ne nécessite pas de vote du CS. Vous pouvez l'envoyer directement.
                            </p>
                            {!selectedMessageCS.destinataireEmail && (
                              <p className="text-sm text-amber-600 mb-3">
                                ⚠️ Renseignez l'email du destinataire avant d'envoyer.
                              </p>
                            )}
                            <button
                              onClick={handleEnvoiDirect}
                              disabled={isSendingDirect || !connectionStatus.isConnected || !selectedMessageCS.destinataireEmail}
                              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {isSendingDirect ? (
                                <>
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                  Envoi en cours...
                                </>
                              ) : (
                                <>
                                  <Send className="w-4 h-4" />
                                  Envoyer directement
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Section votes */}
                    <div className="border-t border-gray-100 p-4">
                      <h3 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        Votes du Conseil Syndical
                      </h3>

                      {/* Liste des votes */}
                      {selectedMessageCS.validationCS && selectedMessageCS.validationCS.votes.length > 0 && (
                        <div className="space-y-2 mb-4">
                          {selectedMessageCS.validationCS.votes.map((v, idx) => (
                            <div key={idx} className="flex items-center gap-3 text-sm">
                              {v.vote === 'pour' ? (
                                <ThumbsUp className="w-4 h-4 text-green-600" />
                              ) : (
                                <ThumbsDown className="w-4 h-4 text-red-600" />
                              )}
                              <span className="font-medium">{v.membreNom}</span>
                              <span className={v.vote === 'pour' ? 'text-green-600' : 'text-red-600'}>
                                {v.vote === 'pour' ? 'Approuve' : 'Refuse'}
                              </span>
                              {v.commentaire && (
                                <span className="text-gray-500 italic">"{v.commentaire}"</span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Interface de vote (si pas encore vote) */}
                      {!hasUserVoted(selectedMessageCS) && selectedMessageCS.statut === 'en_validation' && (
                        <div className="space-y-3">
                          <div>
                            <label className="block text-sm text-gray-600 mb-1">
                              Commentaire (optionnel)
                            </label>
                            <input
                              type="text"
                              value={voteComment}
                              onChange={(e) => setVoteComment(e.target.value)}
                              placeholder="Ajouter un commentaire..."
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                            />
                          </div>
                          <div className="flex gap-3">
                            <button
                              onClick={() => handleVote('pour')}
                              disabled={isVoting}
                              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors disabled:opacity-50"
                            >
                              {isVoting ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <ThumbsUp className="w-4 h-4" />
                              )}
                              Approuver
                            </button>
                            <button
                              onClick={() => handleVote('contre')}
                              disabled={isVoting}
                              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50"
                            >
                              {isVoting ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <ThumbsDown className="w-4 h-4" />
                              )}
                              Refuser
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Message si deja vote */}
                      {hasUserVoted(selectedMessageCS) && (
                        <div className={`p-3 rounded-lg text-sm ${
                          getUserVote(selectedMessageCS) === 'pour' 
                            ? 'bg-green-50 text-green-800' 
                            : 'bg-red-50 text-red-800'
                        }`}>
                          Vous avez {getUserVote(selectedMessageCS) === 'pour' ? 'approuve' : 'refuse'} ce message.
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="flex-1 flex items-center justify-center text-gray-500">
                    <div className="text-center">
                      <Clock className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                      <p>Selectionnez un message pour voter</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* ================================================================
          MODALES
      ================================================================ */}

      {/* Modale d'archivage */}
      {showArchiveModal && selectedEmail && coproprieteId && (
        <ArchiveEmailModal
          email={selectedEmail}
          coproprieteId={coproprieteId}
          onClose={() => setShowArchiveModal(false)}
          onSuccess={() => handleArchiveSuccess('Email archive dans la GED')}
        />
      )}

      {/* Modale de reponse */}
      {showReplyModal && selectedEmail && coproprieteId && (
        <ReplyEmailModal
          email={selectedEmail}
          coproprieteId={coproprieteId}
          onClose={() => setShowReplyModal(false)}
          onSuccess={handleReplySuccess}
        />
      )}

      {/* Modale nouveau message */}
      {showComposeModal && coproprieteId && (
        <ComposeEmailModal
          coproprieteId={coproprieteId}
          onClose={() => {
            setShowComposeModal(false);
            setSentinelleEmailPrefill(null);
          }}
          onSuccess={handleComposeSuccess}
          onTransformToCourrier={handleTransformToCourrier}
          prefill={sentinelleEmailPrefill || undefined}
        />
      )}

      {/* Modale de suppression */}
      {showDeleteModal && selectedEmail && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 rounded-full bg-red-100">
                <Trash2 className="w-6 h-6 text-red-600" />
              </div>

              <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                Supprimer cet email ?
              </h3>

              <p className="text-gray-600 text-center mb-2">
                L'email sera mis dans la corbeille Gmail.
              </p>
              <p className="text-sm font-medium text-gray-900 text-center mb-4 px-4 py-2 bg-gray-100 rounded truncate">
                {selectedEmail.subject}
              </p>
              <p className="text-sm text-gray-500 text-center mb-6">
                Il restera recuperable pendant 30 jours dans Gmail.
              </p>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowDeleteModal(false)}
                  disabled={isDeleting}
                  className="flex-1 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  onClick={handleDeleteEmail}
                  disabled={isDeleting}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isDeleting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Suppression...
                    </>
                  ) : (
                    'Supprimer'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MessageriePage;
